import React, { useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import OrgContext from '../context/OrgContext';

const Menus = () => {
    const { activeOrg, user } = useContext(OrgContext);

    const [menus, setMenus] = useState([]);
    const [newMenu, setNewMenu] = useState({ name: '', products: [], places: [], availableFrom: '', availableTo: '' });
    const [products, setProducts] = useState([]);
    const [places, setPlaces] = useState([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [productsFilter, setProductsFilter] = useState('');
    const [placesFilter, setPlacesFilter] = useState('');
    const [loading, setLoading] = useState(true); // Loading state for data fetching

    useEffect(() => {
        if (activeOrg) {
            setLoading(true); // Start loading when activeOrg changes
            fetchMenus();
            fetchProducts();
            fetchPlacesFromContext();
        }
    }, [activeOrg]);

    const fetchMenus = async () => {
        const token = sessionStorage.getItem('token');
        try {
            const response = await axios.get(`${process.env.REACT_APP_SRV}/menus`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    orgid: activeOrg?._id || ''
                },
            });
            setMenus(response.data || []);
        } catch (error) {
            toast.error('Error fetching menus.');
        } finally {
            setLoading(false);
        }
    };

    const fetchProducts = async () => {
        const token = sessionStorage.getItem('token');
        try {
            const response = await axios.get(`${process.env.REACT_APP_SRV}/products`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    orgid: activeOrg?._id || ''
                },
            });
            setProducts(response.data || []);
        } catch (error) {
            toast.error('Error fetching products.');
        }
    };

    const fetchPlacesFromContext = () => {
        setPlaces(user?.locationIDs || []);
    };

    const openDialog = () => {
        setNewMenu({ name: '', products: [], places: [], availableFrom: '', availableTo: '' });
        setIsDialogOpen(true);
    };

    const editMenu = (menu) => {
        setNewMenu({
            _id: menu._id,
            name: menu.name,
            products: menu.products || [],
            places: menu.places || [],
            availableFrom: menu.availableFrom || '',
            availableTo: menu.availableTo || '',
        });
        setIsDialogOpen(true);
    };

    const handleCreateOrUpdate = async () => {
        const token = sessionStorage.getItem('token');
        const sanitizedMenu = {
            ...newMenu,
            products: newMenu.products.filter(productId => productId !== null),
            places: newMenu.places.filter(placeId => placeId !== null),
        };

        try {
            const endpoint = sanitizedMenu._id
                ? `${process.env.REACT_APP_SRV}/menus/${sanitizedMenu._id}`
                : `${process.env.REACT_APP_SRV}/menus`;
            const method = sanitizedMenu._id ? 'put' : 'post';

            const response = await axios[method](endpoint, sanitizedMenu, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    orgid: activeOrg?._id || ''
                },
            });

            if (sanitizedMenu._id) {
                setMenus(menus.map(menu => menu._id === response.data._id ? response.data : menu));
                toast.success('Menu updated successfully!');
            } else {
                setMenus([...menus, response.data]);
                toast.success('Menu created successfully!');
            }
            fetchMenus();
            setIsDialogOpen(false);
        } catch (error) {
            toast.error(`Error ${sanitizedMenu._id ? 'updating' : 'creating'} menu.`);
        }
    };

    // Memoized filtered lists to improve performance
    const filteredProducts = useMemo(() => {
        return products.filter(product => product.name.toLowerCase().includes(productsFilter));
    }, [products, productsFilter]);

    const filteredPlaces = useMemo(() => {
        return places.filter(place => place.name.toLowerCase().includes(placesFilter));
    }, [places, placesFilter]);

    return (
        <div>
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
            <div className="mb-4 flex justify-between items-center">
                <button onClick={openDialog} className="bg-green-600 px-4 py-2 rounded hover:bg-green-700">
                    Add Menu
                </button>
            </div>

            {loading ? (
                <p>Loading...</p>
            ) : (
                <ul>
                    {menus.map(menu => (
                        <li key={menu._id} className="bg-gray-800 p-4 mb-2 rounded">
                            <h3 className="text-xl">{menu.name}</h3>
                            <p className="text-gray-400">
                                Products: {menu.products?.length > 0
                                    ? menu.products.map(productId => {
                                        const product = products.find(r => r._id === productId);
                                        return product ? product.name : ''; // Return an empty string if not found
                                    }).filter(name => name !== '').join(', ') || 'No products' // Join names or show fallback text
                                    : 'No products'}
                            </p>

                            <p className="text-gray-400">
                                Places: {menu.places?.length > 0
                                    ? menu.places.map(placeId => places.find(p => p._id === placeId)?.name || 'Loading...').join(', ')
                                    : 'No places'}
                            </p>
                            <p className="text-gray-400">
                                Available From: {menu.availableFrom} - Available To: {menu.availableTo}
                            </p>
                            <div className="flex">
                                <button onClick={() => editMenu(menu)} className="bg-blue-600 px-4 py-2 rounded hover:bg-blue-700 mr-2">
                                    Edit
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {isDialogOpen && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center">
                    <div className="bg-gray-800 p-6 rounded-lg w-2/3">
                        <h3 className="text-xl mb-4">{newMenu._id ? 'Edit Menu' : 'Add New Menu'}</h3>
                        <input
                            type="text"
                            placeholder="Menu Name"
                            value={newMenu.name}
                            onChange={(e) => setNewMenu({ ...newMenu, name: e.target.value })}
                            className="block w-full mb-4 p-2 bg-gray-700 text-white rounded"
                        />
                        <div className="flex justify-between mb-4">
                            <div className="w-1/2 pr-2">
                                <label className="text-white mb-2 block">Available From</label>
                                <input
                                    type="time"
                                    value={newMenu.availableFrom}
                                    onChange={(e) => setNewMenu({ ...newMenu, availableFrom: e.target.value })}
                                    className="block w-full p-2 bg-gray-700 text-white rounded"
                                />
                            </div>
                            <div className="w-1/2 pl-2">
                                <label className="text-white mb-2 block">Available To</label>
                                <input
                                    type="time"
                                    value={newMenu.availableTo}
                                    onChange={(e) => setNewMenu({ ...newMenu, availableTo: e.target.value })}
                                    className="block w-full p-2 bg-gray-700 text-white rounded"
                                />
                            </div>
                        </div>

                        <label className="text-white mb-2 block">Select Products</label>
                        <input
                            type="text"
                            placeholder="Search products..."
                            className="block w-full p-2 mb-2 bg-gray-700 text-white rounded"
                            onChange={(e) => setProductsFilter(e.target.value.toLowerCase())}
                        />
                        <div className="h-40 overflow-y-auto border border-gray-600 p-2">
                            {filteredProducts.map(product => (
                                <div key={product._id} className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={newMenu.products.includes(product._id)}
                                        onChange={() =>
                                            setNewMenu({
                                                ...newMenu,
                                                products: newMenu.products.includes(product._id)
                                                    ? newMenu.products.filter(id => id !== product._id)
                                                    : [...newMenu.products, product._id],
                                            })
                                        }
                                        className="mr-2"
                                    />
                                    <span>{product.name}</span>
                                </div>
                            ))}
                        </div>

                        <label className="text-white mb-2 block">Select Places</label>
                        <input
                            type="text"
                            placeholder="Search places..."
                            className="block w-full p-2 mb-2 bg-gray-700 text-white rounded"
                            onChange={(e) => setPlacesFilter(e.target.value.toLowerCase())}
                        />
                        <div className="h-40 overflow-y-auto border border-gray-600 p-2">
                            {filteredPlaces.map(place => (
                                <div key={place._id} className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={newMenu.places.includes(place._id)}
                                        onChange={() =>
                                            setNewMenu({
                                                ...newMenu,
                                                places: newMenu.places.includes(place._id)
                                                    ? newMenu.places.filter(id => id !== place._id)
                                                    : [...newMenu.places, place._id],
                                            })
                                        }
                                        className="mr-2"
                                    />
                                    <span>{place.name}</span>
                                </div>
                            ))}
                        </div>

                        <button onClick={handleCreateOrUpdate} className="bg-green-600 px-4 py-2 rounded hover:bg-green-700">
                            {newMenu._id ? 'Update Menu' : 'Create Menu'}
                        </button>
                        <button onClick={() => setIsDialogOpen(false)} className="bg-red-600 px-4 py-2 ml-2 rounded hover:bg-red-700">
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Menus;
